// frontend/src/components/common/NotificationSettings.tsx
// Push notification category toggles (NOTIFICATIONS_SPEC.md): master switch +
// the categories. Opt-out model: everything defaults ON server-side, except
// 'starters' (opt-in, default OFF).
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import {
  fetchNotificationPrefs,
  updateNotificationPrefs,
  NotificationPrefs,
} from '@/api/profile';

const CATEGORY_LABELS: Array<{ key: keyof NotificationPrefs; label: string; hint: string }> = [
  { key: 'transactions', label: 'Trades & Waivers', hint: 'Trade offers and results, waiver claim outcomes' },
  { key: 'lineups', label: 'Lineup Alerts', hint: 'Open roster slots and inactive starters before kickoff' },
  { key: 'draft', label: 'Draft Alerts', hint: 'One hour warning and draft start' },
  { key: 'dfs', label: 'Daily Contests', hint: 'Contest reminders and final results' },
  // Opt-in (default OFF): pure confirmation, no action. User-requested 2026-08-26.
  { key: 'starters', label: 'Starting Confirmations', hint: 'When team sheets drop and your starter is in the XI. Off by default.' },
];

const Toggle: React.FC<{ on: boolean; disabled?: boolean; onChange: () => void; label: string }> = ({
  on, disabled, onChange, label,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={disabled}
    onClick={onChange}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow ${
      on ? 'bg-accent-yellow' : 'bg-gray-600'
    } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
  >
    <span
      className={`inline-block h-5 w-5 mt-0.5 rounded-full bg-white shadow transform transition-transform duration-150 ${
        on ? 'translate-x-5' : 'translate-x-0.5'
      }`}
    />
  </button>
);

const NotificationSettings: React.FC = () => {
  const { token } = useAuth();
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetchNotificationPrefs(token)
      .then(setPrefs)
      .catch(() => setPrefs({ master: true, transactions: true, lineups: true, dfs: true, draft: true, starters: false }));
  }, [token]);

  const flip = async (key: keyof NotificationPrefs) => {
    if (!token || !prefs || isSaving) return;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next); // optimistic
    setIsSaving(true);
    try {
      const saved = await updateNotificationPrefs(token, { [key]: next[key] });
      setPrefs(saved);
    } catch {
      setPrefs(prefs); // revert
      toast.error('Could not save notification settings.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!prefs) return null;

  return (
    <section className="bg-surface-card p-4 md:p-6 rounded-lg shadow-xl mb-8">
      <h2 className="font-heading text-2xl text-accent-yellow mb-4">Notifications</h2>

      <div className="flex items-center justify-between py-2">
        <div>
          <p className="text-sm font-semibold text-gray-100">Push Notifications</p>
          <p className="text-xs text-gray-400">Master switch for all notifications</p>
        </div>
        <Toggle on={prefs.master} onChange={() => flip('master')} label="Push notifications" />
      </div>

      <div className="border-t border-gray-700/50 mt-2 pt-2 space-y-1">
        {CATEGORY_LABELS.map(({ key, label, hint }) => (
          <div key={key} className="flex items-center justify-between py-2">
            <div>
              <p className={`text-sm font-semibold ${prefs.master ? 'text-gray-100' : 'text-gray-500'}`}>{label}</p>
              <p className="text-xs text-gray-400">{hint}</p>
            </div>
            <Toggle
              on={prefs[key]}
              disabled={!prefs.master}
              onChange={() => flip(key)}
              label={label}
            />
          </div>
        ))}
      </div>
    </section>
  );
};

export default NotificationSettings;
